/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useMemo, useRef, useState } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'
import { getCSSVariableValue } from '../../../assets/ts/_utils'
import { useThemeMode } from '../../layout/theme-mode/ThemeModeProvider'
import { statusNewOldOrders } from '../../../../api/ordersApi'
import dayjs from 'dayjs'
import 'dayjs/locale/it'

dayjs.locale('it')

type Props = {
  className: string
  color: string
}

type FilterRange = '2settimane' | '1mese' | '2mesi' | '3mesi'

interface OrderEvent {
  data: string
  ID_Ordine: number
  Nome_Azienda: string
  Importo_Contratto: number
  Numero_visori: number
  Tipo: 'Nuovo' | 'Inattivo'
  EntrataDettaglio: 'Rinnovo' | 'Nuovo'
}

const ITEMS_PER_PAGE = 3

const StatisticheOrdiniDettagli2: React.FC<Props> = ({ className, color }) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()

  const [rows, setRows] = useState<OrderEvent[]>([])
  const [filter, setFilter] = useState<FilterRange>('1mese')

  const [pageNuovi, setPageNuovi] = useState(1)
  const [pageInattivi, setPageInattivi] = useState(1)

  type FilterEntrati = 'tutti' | 'nuovi' | 'rinnovi'
  const [filterEntrati, setFilterEntrati] = useState<FilterEntrati>('nuovi')

  // ===========================
  // FETCH
  // ===========================
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await statusNewOldOrders()
  
        // ✅ normalizzazione robusta: accetta array o oggetti comuni (axios/fetch)
        const data =
          Array.isArray(response) ? response :
          Array.isArray((response as any)?.data) ? (response as any).data :
          Array.isArray((response as any)?.rows) ? (response as any).rows :
          Array.isArray((response as any)?.data?.rows) ? (response as any).data.rows :
          []
  
        setRows(data)
      } catch (e) {
        console.error('Errore fetch entrati/usciti:', e)
        setRows([]) // evita crash
      }
    }
  
    fetchData()
  }, [])

  // ===========================
  // FILTRO TEMPORALE
  // ===========================
  const filteredRows = useMemo(() => {
    const today = dayjs()
    let limit = today.subtract(1, 'month')

    if (filter === '2settimane') limit = today.subtract(2, 'week')
    if (filter === '2mesi') limit = today.subtract(2, 'month')
    if (filter === '3mesi') limit = today.subtract(3, 'month')

    return rows.filter(r => dayjs(r.data).isAfter(limit, 'day'))
  }, [rows, filter])

  // reset pagine quando cambia filtro
  useEffect(() => {
    setPageNuovi(1)
    setPageInattivi(1)
  }, [filter])

  useEffect(() => {
    setPageNuovi(1)
  }, [filterEntrati])

  // ===========================
  // SEPARAZIONE DATI
  // ===========================
  const entratiRows = useMemo(
    () => filteredRows.filter(r => r.Tipo === 'Nuovo'),
    [filteredRows]
  )
  
  const entratiRowsFiltrati = useMemo(() => {
    if (filterEntrati === 'tutti') return entratiRows
    if (filterEntrati === 'nuovi') return entratiRows.filter(r => r.EntrataDettaglio === 'Nuovo')
    return entratiRows.filter(r => r.EntrataDettaglio === 'Rinnovo')
  }, [entratiRows, filterEntrati])

  const inattiviRows = useMemo(
    () => filteredRows.filter(r => r.Tipo === 'Inattivo'),
    [filteredRows]
  )

  // ===========================
  // TOTALI
  // ===========================
  const totaleImportoNuovi = useMemo(
    () => entratiRowsFiltrati.reduce((s, r) => s + Number(r.Importo_Contratto), 0),
    [entratiRowsFiltrati]
  )
  
  const totaleVisoriNuovi = useMemo(
    () => entratiRowsFiltrati.reduce((s, r) => s + Number(r.Numero_visori), 0),
    [entratiRowsFiltrati]
  )
  
  const totalPagesNuovi = useMemo(
    () => Math.max(1, Math.ceil(entratiRowsFiltrati.length / ITEMS_PER_PAGE)),
    [entratiRowsFiltrati.length]
  )
  
  const nuoviPageData = useMemo(() => {
    const start = (pageNuovi - 1) * ITEMS_PER_PAGE
  
    // più recente prima
    const sorted = [...entratiRowsFiltrati].sort(
      (a, b) => dayjs(b.data).valueOf() - dayjs(a.data).valueOf()
    )
  
    return sorted.slice(start, start + ITEMS_PER_PAGE)
  }, [entratiRowsFiltrati, pageNuovi])

  const totaleImportoInattivi = useMemo(
    () => inattiviRows.reduce((s, r) => s + Number(r.Importo_Contratto), 0),
    [inattiviRows]
  )

  const totaleVisoriInattivi = useMemo(
    () => inattiviRows.reduce((s, r) => s + Number(r.Numero_visori), 0),
    [inattiviRows]
  )

  // ===========================
  // PAGINAZIONE
  // ===========================

  const inattiviPageData = useMemo(() => {
    const start = (pageInattivi - 1) * ITEMS_PER_PAGE
  
    // 1) Ordina dal più vicino ad oggi al più lontano
    const sorted = [...inattiviRows].sort(
      (a, b) => dayjs(a.data).valueOf() - dayjs(b.data).valueOf()
    )
  
    // 2) Paginazione sulla lista già ordinata
    return sorted.slice(start, start + ITEMS_PER_PAGE)
  }, [inattiviRows, pageInattivi])

  // ===========================
  // TOTAL PAGES
  // ===========================

  const totalPagesInattivi = useMemo(
    () => Math.max(1, Math.ceil(inattiviRows.length / ITEMS_PER_PAGE)),
    [inattiviRows.length]
  )

  // se per qualche motivo resti su una pagina "troppo alta" dopo filtri/refresh
  useEffect(() => {
    if (pageNuovi > totalPagesNuovi) setPageNuovi(totalPagesNuovi)
  }, [pageNuovi, totalPagesNuovi])

  useEffect(() => {
    if (pageInattivi > totalPagesInattivi) setPageInattivi(totalPagesInattivi)
  }, [pageInattivi, totalPagesInattivi])

  // ===========================
  // GRAFICO
  // ===========================
  useEffect(() => {
    if (!chartRef.current) return

    const chart = new ApexCharts(
      chartRef.current,
      getChartOptions(350, filteredRows)
    )

    chart.render()
    return () => chart.destroy()
  }, [chartRef, mode, filteredRows])

  // ===========================
  // RENDER
  // ===========================
  return (
    <div className={`card ${className}`}>
      {/* HEADER */}
      <div className="card-header border-0 pt-5 d-flex justify-content-between">
        <h3 className="card-title">
          <span className="card-label fw-bold fs-3">
            Contratti entrati/non rinnovati
          </span>
        </h3>

        <div className="card-toolbar">
          <span className="me-3">ULTIMI</span>
          {[
            ['2settimane', '15 giorni'],
            ['1mese', '30 giorni'],
            ['2mesi', '2 mesi'],
            ['3mesi', '3 mesi'],
          ].map(([k, l]) => (
            <a
              key={k}
              className={`btn btn-sm btn-color-muted btn-active btn-active-primary px-4 ms-1 ${
                filter === k ? 'active' : ''
              }`}
              onClick={() => setFilter(k as FilterRange)}
            >
              {l}
            </a>
          ))}
        </div>
      </div>

      {/* BODY */}
      <div className="card-body pt-0">
        {/* GRAFICO */}
        <div ref={chartRef} style={{ height: '350px' }} />

        {/* DUE COLONNE */}
        <div className="row g-6 mt-6">

          {/* ===== SINISTRA: NUOVI ===== */}
          <div className="col-md-6 d-flex flex-column">
          <Totali
            color={color}
            importo={totaleImportoNuovi}
            visori={totaleVisoriNuovi}
          >
            <div className="d-flex justify-content-center gap-2">
              {[
                ['tutti', 'Tutti'],
                ['nuovi', 'Nuovi'],
                ['rinnovi', 'Rinnovi'],
              ].map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  className={`btn btn-sm btn-light ${filterEntrati === k ? 'active' : ''}`}
                  onClick={() => setFilterEntrati(k as any)}
                >
                  {l}
                </button>
              ))}
            </div>
          </Totali>

            <Lista rows={nuoviPageData} />

            <Pagination
              currentPage={pageNuovi}
              totalPages={totalPagesNuovi}
              onChange={(p) => {
                if (p < 1 || p > totalPagesNuovi) return
                setPageNuovi(p)
              }}
            />
          </div>

          {/* ===== DESTRA: INATTIVI ===== */}
          <div className="col-md-6 d-flex flex-column">
            <Totali
              color="danger"
              importo={totaleImportoInattivi}
              visori={totaleVisoriInattivi}
            >
              <div className="d-flex justify-content-center gap-2">
              {[
                ['tutti', 'Tutti'],
                ['nuovi', 'Nuovi'],
                ['rinnovi', 'Rinnovi'],
              ].map(([k, l]) => (
                <button
                  key={k}
                  type="button"
                  className={`btn btn-sm btn-light ${filterEntrati === k ? 'active' : ''}`}
                  onClick={() => setFilterEntrati(k as any)}
                >
                  {l}
                </button>
              ))}
            </div>
            </Totali>

            <Lista rows={inattiviPageData} />

            <Pagination
              currentPage={pageInattivi}
              totalPages={totalPagesInattivi}
              onChange={(p) => {
                if (p < 1 || p > totalPagesInattivi) return
                setPageInattivi(p)
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export { StatisticheOrdiniDettagli2 }

const getPageNumbers = (total: number, current: number): (number | string)[] => {
  const pages: (number | string)[] = []
  const add = (n: number | string) => pages.push(n)

  add(1)

  if (current > 3) add('...')

  if (current - 1 > 1) add(current - 1)
  if (current !== 1 && current !== total) add(current)
  if (current + 1 < total) add(current + 1)

  if (current < total - 2) add('...')

  if (total > 1) add(total)

  return pages.filter((value, index, self) => self.indexOf(value) === index)
}

const Pagination = ({
  currentPage,
  totalPages,
  onChange,
}: {
  currentPage: number
  totalPages: number
  onChange: (page: number) => void
}) => (
  <div
    className='pagination-container d-flex justify-content-center align-items-center mt-3'
    style={{ fontSize: '0.8rem' }}
  >
    {/* FRECCIA PRECEDENTE */}
    <button
      className='btn btn-outline-secondary btn-sm me-2'
      disabled={currentPage === 1}
      onClick={() => onChange(currentPage - 1)}
    >
      ‹
    </button>

    {/* NUMERI DINAMICI */}
    {getPageNumbers(totalPages, currentPage).map((item, index) =>
      item === '...' ? (
        <span key={index} className='mx-1 fw-bold fs-5'>
          …
        </span>
      ) : (
        <button
          key={index}
          className={`btn btn-outline-secondary btn-sm mx-1 ${
            currentPage === item ? 'active' : ''
          }`}
          onClick={() => onChange(Number(item))}
        >
          {item}
        </button>
      )
    )}

    {/* FRECCIA SUCCESSIVA */}
    <button
      className='btn btn-outline-secondary btn-sm ms-2'
      disabled={currentPage === totalPages || totalPages === 0}
      onClick={() => onChange(currentPage + 1)}
    >
      ›
    </button>
  </div>
)

//
// ===========================
// COMPONENTI DI SUPPORTO
// ===========================
//

const Totali = ({
  color,
  importo,
  visori,
  children,
}: {
  color: string
  importo: number
  visori: number
  children?: React.ReactNode
}) => (
  <div className={`px-9 pt-5 pb-5 card-rounded bg-${color}`}>
    {/* HEADER sempre presente: se non ci sono children, spazio vuoto */}
    <div className="d-flex justify-content-end mb-3" style={{ minHeight: '32px' }}>
      {children ?? <span />} 
    </div>

    <div className="d-flex justify-content-center gap-20">
      <div className="text-white text-center">
        <span className="fw-semibold fs-1 d-block">Importo</span>
        <span className="fw-bold fs-2x">
          €{importo.toLocaleString('it-IT', { minimumFractionDigits: 2 })}
        </span>
      </div>

      <div className="text-white text-center">
        <span className="fw-semibold fs-1 d-block">Licenze</span>
        <span className="fw-bold fs-2x">{visori}</span>
      </div>
    </div>
  </div>
)

const Lista = ({ rows }: { rows: OrderEvent[] }) => (
  <div className="table-responsive px-6 pt-5 pb-3">
    <table className="table align-middle gy-4">
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            <td className="min-w-200px">
              <div className="fw-bold fs-5">{r.Nome_Azienda}</div>
              <div className="text-muted fs-7">
                {dayjs(r.data).format('LL')}
              </div>
            </td>

            <td className="text-center fw-bold">
              {r.Numero_visori} licenze
            </td>

            <td className="text-end fw-bold">
              €{r.Importo_Contratto.toLocaleString('it-IT', {
                minimumFractionDigits: 2,
              })}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

//
// ===========================
// CHART OPTIONS
// ===========================
//

function getChartOptions(
  height: number,
  rows: OrderEvent[]
): ApexOptions {
  const labelColor = getCSSVariableValue('--kt-gray-500')
  const primary = getCSSVariableValue('--kt-primary')
  const danger = getCSSVariableValue('--kt-danger')
  const secondaryColor = getCSSVariableValue('--kt-success')

  const map: Record<string, { nuovi: number; inattivi: number }> = {}

  rows.forEach(r => {
    if (!map[r.data]) map[r.data] = { nuovi: 0, inattivi: 0 }
    if (r.Tipo === 'Nuovo') map[r.data].nuovi++
    if (r.Tipo === 'Inattivo') map[r.data].inattivi++
  })

  const dates = Object.keys(map).sort()

  return {
    series: [
      { name: 'Contratti entrati', type: 'column', data: dates.map(d => map[d].nuovi) },
      { name: 'Contratti non rinnovati', type: 'line', data: dates.map(d => map[d].inattivi) },
    ],
    chart: {
      height,
      type: 'line',
      fontFamily: 'inherit',
      zoom: { enabled: false },
      toolbar: { show: false },
    },
    stroke: { curve: 'smooth', width: [0, 3] },
    plotOptions: { bar: { columnWidth: '40%', borderRadius: 4 } },
    xaxis: {
      categories: dates.map(d => dayjs(d).format('DD/MM')),
      labels: { style: { colors: labelColor, fontSize: '14px' } },
      axisTicks: { show: false },
      axisBorder: { show: false },
      tooltip: { enabled: false },
    },
    yaxis: [
      {
        labels: {
          formatter: (val: number) => Math.round(val).toString(),
        },
      },
      {
        opposite: true,
        labels: {
          formatter: (val: number) => Math.round(val).toString(),
        },
      },
    ],
    colors: [secondaryColor, danger],
    legend: {
      show: true,
      fontSize: '20px',
      offsetY: 0,
    },
    grid: {
      strokeDashArray: 4,
      yaxis: { lines: { show: true } },
      padding: { bottom: 30 }, // evita taglio legenda
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: {
        formatter: (val: number) => Math.round(val).toString(),
      },
    },
  }
}
